import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type Dispatch, type SetStateAction } from 'react'
import { ArrowDown, ClipboardPaste, Layers, Plus, X } from 'lucide-react'
import type { Card } from '../types'
import { useLocale } from '../i18n/context'
import { DeckAnchorNotice } from './DeckAnchorNotice'
import { useFillAllFlow, useFillSelectionFlow } from '../hooks/useDeckFillFlows'
import { useViewportBottomGap } from '../hooks/useViewportBottomGap'
import { useHistorySentinel } from '../hooks/useHistorySentinel'
import { useMeasuredHeight } from '../hooks/useMeasuredHeight'
import { getAppScrollElement } from '../utils/appScroll'
import { caretKeepDelta, focusDelta } from '../utils/deckFocusPositioning'
import { focusedCaretKeepBottom } from '../utils/caretGeometry'
import { createScrollMotion, PLACEMENT_DURATION, type ScrollMotion } from '../utils/deckFocusMotion'
import {
  TERM_FIELD_TYPE, buildCardIndexMap, counterGrammar, counterTotal, findFieldKeyOfType,
  stepTarget, type FieldType,
} from '../utils/deckCardNavigation'
import { DECK_TOP_STRIP_CLEARANCE, DECK_TOP_STRIP_HEIGHT, DECK_TOP_STRIP_PROBE_ATTR } from './DeckTopStrip'
import { filterCards, type CardFilterChip } from '../utils/cardFilters'
import { useCardSearchInfinite } from '../hooks/useCards'
import {
  EMPTY_SELECTION, isCardSelected, isSelectionActive, selectWholeDeck, selectionCount,
  selectionFilterChanged, setCardSelected, toggleCardSelected,
  type DeckCardCounts, type DeckSelection,
} from '../utils/deckSelection'
import { useLookupPaletteStore } from '../stores/lookupPaletteStore'
import { fieldTypeLabel } from '../utils/cardSideDisplay'
import VocabCard, { TERM_FIELD_KEY } from './shared/VocabCard'
import ActiveVocabCard, { type ActiveVocabCardHandle } from './shared/ActiveVocabCard'
import FieldTypeChooser from './FieldTypeChooser'
import DeckViewBar from './DeckViewBar'
import DeckEditBar from './DeckEditBar'
import DeckSearchPanel, { type DeckSearchStatus } from './DeckSearchPanel'
import DeckFilterChipsBar from './DeckFilterChipsBar'
import FillAllSheet from './FillAllSheet'
import FillAllProgressBar from './FillAllProgressBar'
import SelectModeActionBar, { SelectModeHeader } from './SelectModeBar'

/** Stands in for the unsaved new card's id in `activation` — it has no server
 * id yet, and using the empty string would collide with nothing but read as a
 * real lookup miss. */
const DRAFT_CARD_ID = '__draft__'

/**
 * Marks every fixed element that occupies the bottom slot — view bar, edit bar,
 * select bar, and the chooser/transient strip riding above them. The
 * focus positioning below finds the bottom of the free space by measuring the
 * highest of whatever is actually mounted, so a further pinned element joins by
 * carrying this attribute and needs no arithmetic of its own. Height reserving
 * (useMeasuredHeight, summed into one `paddingBottom`) is the same single
 * mechanism seen from the other side and stays as it is.
 */
const PINNED_PROBE_ATTR = 'data-deck-pinned'
const PINNED_PROBE = { [PINNED_PROBE_ATTR]: '' }

/** Breathing gap between the top strip and a field aligned against it. Cosmetic
 * only — the free space itself is measured, never assumed. */
const FOCUS_TOP_GAP = 8

interface Props {
  deckId: string
  deckLanguage: string
  cards: Card[]
  /**
   * The deck's TRUE size, from the deck record — not `cards.length`, which is
   * only the pages fetched so far. §13's counter says how big the deck is, and
   * a counter reading `30` on a 93-card deck until it has been scrolled is the
   * loaded-set defect family of brief §23 item 9 / ADR-067 Decision 6. That
   * family is not fixed here (`Select all`, Delete and Move still take the
   * loaded set); this makes sure the new counter does not join it.
   */
  deckCardCount: number
  isOwner: boolean
  cardsLoading: boolean
  hasNextPage: boolean
  isFetchingNextPage: boolean
  fetchNextPage: () => void
  /**
   * Page size of the feed's own pagination, and the window it currently starts
   * at. Together they turn a card's deck ordinal into a page: quick jump asks
   * the caller to re-anchor at `index / pageSize | 0` + 1, which is ONE request
   * for the card's page instead of a chain of page pulls up to it (brief §21.3
   * item 1). `anchorPage > 1` also means the feed no longer begins at the top of
   * the deck, which the banner below states rather than leaving the reader to
   * infer from a card number.
   */
  pageSize: number
  anchorPage: number
  onAnchorChange: (page: number) => void
  // `navigableCards` is the set the editor's swipe should iterate — the
  // currently-filtered set when opened from a filtered feed, so "Not
  // filled" + swipe becomes a fix-up conveyor (Prompt 8), or the full list
  // otherwise. Snapshotted once at open time by the caller (DeckDetailPage)
  // — see that file for why a live-filtered set would be unsafe here.
  /**
   * The empty deck's import entrance (brief §22): hands the deck off to the
   * existing text-import flow, pre-targeted at THIS deck. The caller owns the
   * navigation because it owns the deck's title, which that flow displays.
   *
   * There is deliberately no `onAddOneByOne` beside it any more: the empty
   * deck's create entrance is `handleAddCard` — the same in-place flow `+ Card`
   * uses — rather than a second screen. And no `onImportCsv`: the brief never
   * specified a CSV control here (§25 names it as unspecified), and CSV import
   * keeps its home in the deck's overflow menu.
   */
  onPasteNote: () => void
  /** Viewing-mode bar: launches Learn for this deck. */
  onStudy: () => void
  /**
   * Editing mode (ADR-067 Decision 2). `editingMode` is the caller's single
   * source of truth for which mode is active — DeckMobileFeed does not own
   * this state, only reacts to it, since the same boolean also governs the
   * top strip's mode word outside this component's tree. `onEnterEditing` is
   * always passed; whether it is offered is decided here, from `isOwner`,
   * together with `+ Card` — not by the caller withholding the handler.
   */
  editingMode: boolean
  onEnterEditing: () => void
  onExitEditing: () => void
  // Prompt 8 — management chrome. All hidden/inert unless summoned via the
  // deck overflow menu (DeckDetailPage), which is what keeps the default
  // feed byte-for-byte the Prompt 4–6 feed.
  searchOpen: boolean
  onCloseSearch: () => void
  /**
   * The scroll position to return to when search closes, captured by
   * DeckDetailPage at the moment search opened — before its own render
   * collapses the scrolling header (§4) that sits above this feed. `null`
   * means there is nothing to restore (search never opened, or a previous
   * restore already consumed it). A ref rather than a number prop: read
   * imperatively by the effect below, on the closing edge of `searchOpen`,
   * and its value changing must not itself cause a render here.
   */
  preSearchScrollTopRef: { current: number | null }
  /** The filter chips' own affordance (§16), independent of search (§15,
   * 2026-08-04 rework) — opened from the ⋯ menu, not the top strip. */
  filterOpen: boolean
  onCloseFilter: () => void
  /** Reports whether a chip other than `All` is narrowing the feed. The active
   * chip lives here, but the ⋯ menu that has to refuse Reorder while a filter
   * is on (brief §4.4) is the page's. */
  onFilterActiveChange?: (active: boolean) => void
  fillAllOpen: boolean
  onCloseFillAll: () => void
  /**
   * What the bulk actions act on (ADR-067 Decision 6, brief §23 item 9).
   *
   * Not a `Set<string>` any more, and that is the item: `Select all` used to
   * collect the ids of the pages this feed had fetched, so Delete and Move
   * acted on 30 cards of a 93-card deck while the counter reported 93. A
   * whole-deck selection now carries no ids at all — see `utils/deckSelection`.
   */
  selection: DeckSelection
  setSelection: Dispatch<SetStateAction<DeckSelection>>
  /** Deck-wide counts from the server. `undefined` while in flight or failed —
   * never substituted with a loaded-set number, which is the whole point. */
  deckCounts: DeckCardCounts | undefined
  countsUnavailable: boolean
  onRequestMove: () => void
  /** Copy the selection into another deck, source untouched (brief §23 item
   * 10). Same page-level picker Move opens, in its copy mode. */
  onRequestCopy: () => void
  onRequestDelete: () => void
  // ── The deep link's arrival ──────────────────────────────────────────────
  //
  // A `?card=` link — the Quick Lookup sheet's own exit, and the phone's usual
  // way into a deck — names one card. The deck screen resolves WHICH card and
  // where it sits; the feed is what moves to it, because the row element only
  // exists here and because the chip and the search that can hide it are this
  // component's own state.
  //
  // Handed over as four plain values rather than one object, so the arrival
  // effect below can depend on them individually and does not re-run on every
  // render of the page.
  /** The link's target, or null when the deck was opened without one. */
  deepLinkCardId: string | null
  /** Changes when the same card is named again (the palette's repeat click),
   * which is what makes a second arrival at one card a second arrival. */
  deepLinkNonce: string | null
  /**
   * Where the target sits in the ANCHORED WINDOW — an index into `cards`, the
   * very array this feed renders from. The index says which card; the row
   * element comes from this render (ADR-067 Decision 6: never a node lookup,
   * and never a scroll offset derived from the index). `null` while the answer
   * is not known yet.
   */
  deepLinkWindowIndex: number | null
  /** Something is still on its way to the window — the locate request, the
   * anchored page, or the re-anchoring itself. While this holds, a target that
   * is not here is not-here-YET; once it clears, it is not coming. */
  deepLinkPending: boolean
  /** The card wearing the mark, owned by the page (both layouts share one
   * mark and one expiry). Styling only — see `VocabCard`. */
  highlightedCardId: string | null
  /** Reports the arrival, which is what starts the mark. */
  onDeepLinkArrived: (cardId: string) => void
}

export default function DeckMobileFeed({
  deckId, deckLanguage, cards, deckCardCount, isOwner, cardsLoading,
  hasNextPage, isFetchingNextPage, fetchNextPage,
  pageSize, anchorPage, onAnchorChange,
  onPasteNote, onStudy,
  editingMode, onEnterEditing, onExitEditing,
  searchOpen, onCloseSearch, preSearchScrollTopRef, filterOpen, onCloseFilter, onFilterActiveChange,
  fillAllOpen, onCloseFillAll,
  selection, setSelection, deckCounts, countsUnavailable,
  onRequestMove, onRequestCopy, onRequestDelete,
  deepLinkCardId, deepLinkNonce, deepLinkWindowIndex, deepLinkPending,
  highlightedCardId, onDeepLinkArrived,
}: Props) {
  const { tr } = useLocale()
  const dd = tr.deckDetail
  const cb = tr.cardBubble
  const ce = tr.cardEditor

  const sentinelRef = useRef<HTMLDivElement>(null)
  // The feed's own outermost node — read once, on search open, purely for
  // `getBoundingClientRect()` (see the scroll effect below). It drives no
  // layout by itself, which is why a plain ref suffices here.
  const feedRootRef = useRef<HTMLDivElement>(null)
  // The search panel needs both a live HEIGHT and a queryable NODE, for two
  // different jobs that must not be conflated:
  //  - `searchPanelHeight` sizes the top reserve (below) so the feed is never
  //    clipped by the panel. `position: fixed` means the panel reserves no
  //    space of its own by default, and its height is not a constant — the
  //    count/status line and the "search everywhere" link only exist once a
  //    query has been typed, and `chipDroppedBySearch`'s notice row is a
  //    further variable term. A value assumed once at open time, or inherited
  //    from the pre-collapse layout the header used to provide, goes stale
  //    the moment the panel's own content changes shape — which is exactly
  //    how the first result ended up clipped behind it once the header
  //    stopped being there to absorb the difference. `useMeasuredHeight` is
  //    the same ResizeObserver-backed mechanism the pinned bottom bars
  //    already reserve space for (below); this is the same idea at the top
  //    edge.
  //  - `searchPanelNodeRef` still answers `getBoundingClientRect()` for the
  //    scroll-motion effect's target, because the panel's `top` is
  //    `DECK_TOP_STRIP_CLEARANCE` — a `calc()` string carrying
  //    `env(safe-area-inset-top)`, not a number arithmetic can use — so the
  //    panel's actual on-screen bottom edge is only ever knowable by reading
  //    the live DOM, the same way `DECK_TOP_STRIP_CLEARANCE`'s own consumers
  //    do.
  // One `<DeckSearchPanel ref={...}>` cannot take two refs, so the callback
  // below fans the mounted node out to both.
  const searchPanelNodeRef = useRef<HTMLDivElement | null>(null)
  const [searchPanelHeightRef, searchPanelHeight] = useMeasuredHeight()
  const searchPanelRef = useCallback((node: HTMLDivElement | null) => {
    searchPanelNodeRef.current = node
    searchPanelHeightRef(node)
  }, [searchPanelHeightRef])
  // Stickiness starts UNEARNED. It used to start asserted, which is what made
  // opening a deck chain every page: the flag was true before the reader had
  // scrolled anything, so the first page's arrival scrolled the feed to its
  // end, that put the sentinel back on screen, and the next page was pulled
  // immediately — four requests on a 93-card deck before the screen was
  // touched. It also landed the reader on the last card rather than on the
  // header with the study launchers, which §25 item 1 ruled out deliberately.
  // Only a real scroll event may grant this; see refreshJumpAffordance.
  const wasNearBottomRef = useRef(false)
  const [showJumpToLatest, setShowJumpToLatest] = useState(false)

  // ── Search + filter chips (brief §15 / §16) ─────────────────────────────
  // Two narrowings with deliberately different scopes and lifetimes:
  //
  //   chips  — persistent, client-side over the loaded pages. They define the
  //            set batch operations act on, which is why they survive a search
  //            and why moving them to the server belongs to the management
  //            layer (implementation item 7) rather than here.
  //   query  — transient, SERVER-side over the WHOLE deck. It narrows only the
  //            walk and exits with one tap.
  //
  // They are never intersected. A search that ran inside an active chip would
  // answer "not in this deck" for a word the deck contains — the same false
  // negative this item exists to remove, merely relocated. §15's answer is to
  // drop the chip and say so, which `chipDroppedBySearch` carries.
  //
  // 2026-08-04 rework: the two also gained separate UI entry points
  // (`DeckSearchPanel` under the top strip's own control; `DeckFilterChipsBar`
  // from the ⋯ menu, unchanged from where it already lived) — bundling them
  // into one panel was what made that panel tall enough to leave few match
  // rows visible under a raised keyboard. The state and the drop/restore
  // mechanics below are unaffected; only which component renders what moved.
  const [query, setQuery] = useState('')
  const [chip, setChip] = useState<CardFilterChip>('all')
  const [chipBeforeSearch, setChipBeforeSearch] = useState<CardFilterChip | null>(null)
  // Chip counts come from the server, over the whole deck. `chipCounts(cards)`
  // counted the pages fetched so far, so `Not filled (12)` on a 93-card deck
  // was a claim about 30 cards wearing the deck's clothes — the same family as
  // §23 item 9, and these numbers sit directly above a Fill action. `null`
  // until they arrive; the chip bar prints no number rather than a wrong one.
  const counts: Record<CardFilterChip, number> | null = deckCounts
    ? {
        all: deckCounts.total,
        'no-translation': deckCounts.no_translation,
        'no-example': deckCounts.no_example,
        'not-filled': deckCounts.not_filled,
      }
    : null

  useEffect(() => {
    onFilterActiveChange?.(chip !== 'all')
  }, [chip, onFilterActiveChange])

  const trimmedQuery = query.trim()
  const searching = searchOpen && trimmedQuery.length > 0
  const search = useCardSearchInfinite(deckId, trimmedQuery, pageSize)

  // Drop an active chip the moment a search starts, and restore it when the
  // search ends — the chip is state the reader set, so search borrows it rather
  // than discarding it.
  useEffect(() => {
    if (searching && chip !== 'all') {
      setChipBeforeSearch(chip)
      setChip('all')
    }
    if (!searching && chipBeforeSearch !== null) {
      setChip(chipBeforeSearch)
      setChipBeforeSearch(null)
    }
    // `chip` is deliberately out of the deps: reacting to it would fight the
    // reader's own chip taps while a search is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searching])

  const searchResults = useMemo(
    () => search.data?.pages.flatMap((p) => p.items) ?? [],
    [search.data],
  )
  /** Deck-wide match count — the FOUND denominator (§13), never a loaded-set count. */
  const searchTotal = search.data?.pages[0]?.total ?? null

  const searchStatus: DeckSearchStatus = !searching
    ? 'idle'
    : search.isError
      ? 'error'
      // `isFetching` rather than `isLoading`: with `placeholderData` holding the
      // previous page, a retyped query is a fetch over stale data, and calling
      // that "results" would print the old count under the new word.
      : (search.isFetching && searchTotal === null) || search.isPending
        ? 'loading'
        : searchTotal === 0
          ? 'empty'
          : 'results'

  // The set the feed renders. While searching this is the SERVER's answer and
  // nothing else — there is no fallback to filtering `cards`, deliberately. A
  // client-side result here would be a partial answer wearing a complete one's
  // clothes, which is worse than the failure it would be papering over.
  //
  // Otherwise it is the chip filter, applied whenever a chip is active — no
  // longer gated on whether the (now separate) chip bar happens to be open.
  // Chips are a persistent narrowing of the feed, not a transient panel's
  // side effect, and `filterCards` with chip `'all'` is `cards` unchanged, so
  // this is one expression rather than a further branch on `filterOpen`.
  const chipFiltered = useMemo(() => filterCards(cards, chip), [cards, chip])
  const displayedCards = searching ? searchResults : chipFiltered

  // An unknown count keeps select mode ON: the reader tapped `Select all` and
  // the mode must not blink off while the count is in flight.
  const selectMode = isSelectionActive(selection, deckCounts)
  const selectedCount = selectionCount(selection, deckCounts)

  // ── The unsaved new card (brief §10) ─────────────────────────────────────
  // `+ Card` cannot POST an empty card — every side's content must be
  // non-empty server-side, Term included — so a new card starts life as a
  // local row and is created by ActiveVocabCard's own commit as soon as its
  // Term has content. Two consequences worth stating, because both are
  // requirements rather than side effects:
  //   • "left empty ⇒ discarded silently" costs nothing: an unsaved row that
  //     goes away was never written, so there is no delete and no undo.
  //   • it is not a claim about stored order. The POST sends no position and
  //     the server appends at max(position)+1 over the WHOLE deck, so a
  //     partially loaded feed cannot land the card mid-deck (open tail #9).
  // `savedId` is filled in once the POST returns, purely so the row is not
  // rendered twice when the refetched page already contains it.
  // `term` seeds the new card's Term. Empty for an ordinary `+ Card`; carrying
  // the query for §15's "add it to this deck" exit, where the reader has
  // already typed the word once and retyping it would be the surface forgetting
  // what the whole interaction was about.
  const [draft, setDraft] = useState<{ key: string; savedId: string | null; term: string } | null>(null)
  // Keyed on `draft.key`, not on `draft`: the object must not change identity
  // when `savedId` is filled in, or the active card would re-render on every
  // save for no reason. Its `id` stays empty on purpose — ActiveVocabCard reads
  // it once, to decide that the first commit is a POST.
  const draftKey = draft?.key ?? null
  const draftTerm = draft?.term ?? ''
  const draftCard = useMemo<Card | null>(() => (draftKey ? {
    id: '', deck_id: deckId, position: 0,
    sides: draftTerm
      ? [{ id: '__draft-term__', label: 'Term', content: draftTerm, position: 0, kind: 'term', form: null }]
      : [],
    mastery_level: null, share_token: null,
    created_at: '', updated_at: '',
  } : null), [draftKey, draftTerm, deckId])

  // The saved card is shown by the draft row itself until that row goes away,
  // so hide the refetched copy for as long as both exist.
  const feedCards = useMemo(
    () => (draft?.savedId ? displayedCards.filter((c) => c.id !== draft.savedId) : displayedCards),
    [displayedCards, draft?.savedId],
  )
  // What `Select all` would select — the DECK's count for the active chip, from
  // the server. It used to be `feedCards.length`, which is why the same deck
  // offered `Select all (30)` on one visit and `Select all (90)` on the next
  // with no scrolling in between (brief §23 item 9). `null` means not yet
  // known, and the label says so rather than printing a stand-in.
  const totalSelectable = selectionCount(selectWholeDeck(chip), deckCounts)

  // ── Editing mode: the active card (ADR-067 Decision 2, brief §5.3) ────────
  // Exactly one card is active and, inside it, exactly one text field holds the
  // caret — which is why this lives here and not in the card: "one field at a
  // time across the whole screen" is a property of the screen. `activation`
  // carries what the tap knew (which card, which field, where in the text) and
  // ActiveVocabCard consumes it once on mount.
  // `missingType` is set only by a §13 walk that landed on a card without a
  // field of the type being carried: the caret goes to the Term (ADR-067
  // Decision A forbids an active card with no field holding it) and the card
  // renders the `+ Add Example` affordance in the missing field's place.
  const [activation, setActivation] = useState<{
    cardId: string; fieldKey: string | null; caret: number | null; missingType?: FieldType
  } | null>(null)
  const activeCardId = editingMode && !selectMode ? activation?.cardId ?? null : null

  // Search opening brings the feed flush against the panel's bottom edge
  // (brief §15, 2026-08-04 rework) — the fix for "opens partly off-screen":
  // the panel itself is `position: fixed` and therefore always fully within
  // the viewport the instant it mounts, but whatever the reader had scrolled
  // to a moment ago is not, and the whole point of the panel is that its
  // match rows are what fills the space below it.
  //
  // Search closing does the mirror job: DeckDetailPage's scrolling header
  // (§4 — identity, Study CTAs, the daily-learning toggle) is unmounted for
  // as long as `searchOpen` is true, a plain conditional render keyed on the
  // same boolean this effect reacts to, so it collapses and reappears in the
  // same render as this effect's two branches run. Restoring it is not
  // enough on its own to put the reader back where they were — the header
  // reappearing changes the document's height under whatever `scrollTop`
  // search left behind — so this glides back to `preSearchScrollTopRef`,
  // captured by the caller at the moment search opened (the last point that
  // still preceded the header's collapse, and therefore the only reliably
  // correct value to return to).
  //
  // One effect, one motion, both directions. Both branches go through
  // `createScrollMotion`, the same module ADR-067 Decision D governs for
  // field-focus placement, rather than a bespoke `scrollTo` or
  // `scrollIntoView` — abort on the reader's own touch/wheel input, never a
  // second writer racing the first. The two branches never run concurrently:
  // `searchOpen` is this effect's only dependency, so React tears down
  // whichever motion the previous branch started (the cleanup below aborts
  // it) before the other branch's body runs, whichever direction the toggle
  // went — there is never more than one `ScrollMotion` instance alive on the
  // scroller. Both branches are skipped while a card is active: that path
  // already owns a motion of its own (the focus-placement effect below), and
  // Decision D forbids two writers on one gesture — the header still
  // collapses/reappears in that branch (DeckDetailPage's conditional render
  // does not consult `activeCardId`), it is only the scroll motion that
  // defers, the same "assistance offered once" scoping the original
  // enter-only version of this effect already used for the entering half.
  useEffect(() => {
    if (activeCardId) return
    const scroller = getAppScrollElement()
    if (!scroller) return

    let target: number
    if (searchOpen) {
      const panelEl = searchPanelNodeRef.current
      const feedEl = feedRootRef.current
      if (!panelEl || !feedEl) return
      target = scroller.scrollTop
        + (feedEl.getBoundingClientRect().top - panelEl.getBoundingClientRect().bottom)
    } else {
      // Nothing captured (search never opened, or a previous close already
      // consumed it) — nothing stranded to walk back to either, since a
      // motion only ever starts on the branch above.
      if (preSearchScrollTopRef.current === null) return
      target = preSearchScrollTopRef.current
      preSearchScrollTopRef.current = null
    }

    const motion = createScrollMotion(scroller)
    motion.to(Math.max(0, target), PLACEMENT_DURATION)

    const yieldToUser = () => { if (motion.inFlight()) motion.abort() }
    scroller.addEventListener('touchmove', yieldToUser, { passive: true })
    scroller.addEventListener('wheel', yieldToUser, { passive: true })
    return () => {
      motion.abort()
      scroller.removeEventListener('touchmove', yieldToUser)
      scroller.removeEventListener('wheel', yieldToUser)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchOpen])

  // Imperative handle onto the mounted ActiveVocabCard (brief §11) — the
  // + Field type chooser lives in this component's own fixed slot, not
  // inside the card, but adding a field is still the card's own draft
  // operation, so the request is dispatched rather than lifted into state.
  const activeCardHandleRef = useRef<ActiveVocabCardHandle>(null)
  const [fieldChooserOpen, setFieldChooserOpen] = useState(false)

  // Editing mode ending, or a selection starting (which replaces the editing
  // bar entirely), leaves no surface for an active card. Unmounting it also
  // flushes its in-flight edit — see ActiveVocabCard's unmount commit — which
  // is exactly what "Done commits what was being typed" means here.
  // Dropping the draft here is also how "the mode exits ⇒ an empty new card is
  // discarded" works: unmounting the row flushes a Term that has content
  // (ActiveVocabCard's unmount commit) and throws away one that does not.
  useEffect(() => {
    if (!editingMode || selectMode) { setActivation(null); setFieldChooserOpen(false); setDraft(null) }
  }, [editingMode, selectMode])

  // The active card left the shown set (deleted, or filtered out from under us).
  // The draft is exempt: it is not in `displayedCards` by construction.
  useEffect(() => {
    setActivation((prev) => (
      prev && prev.cardId !== DRAFT_CARD_ID && !displayedCards.some((c) => c.id === prev.cardId)
        ? null
        : prev
    ))
  }, [displayedCards])

  // Activating a different card retires the draft row — by then its Term was
  // either committed (and the card is a normal deck row) or blank (and there
  // is nothing to keep).
  useEffect(() => {
    if (draft && activation?.cardId !== DRAFT_CARD_ID) setDraft(null)
  }, [activation?.cardId, draft])

  // A different card became active (or none did) — the chooser was scoped to
  // the previous one, so it closes rather than silently retargeting.
  useEffect(() => { setFieldChooserOpen(false) }, [activeCardId])

  // Android back deactivates the active card instead of leaving editing mode
  // (or the deck) outright — one level nested inside DeckDetailPage's own
  // editing-mode sentinel (brief §7.1, same mechanism, reused rather than
  // reinvented). A single press must resolve only this level; see the hook's
  // module comment for why that cannot be decided from `history.state` and is
  // arbitrated centrally instead.
  //
  // The gate is `activeCardId !== null` — "some card is active" — not the
  // identity of the active card. So Prev/Next stepping from one card to the
  // next (`goToCard`) does not re-arm anything: the sentinel spans the whole
  // stretch of the mode and `onClose` (read through a ref) deactivates
  // whichever card is current when the press arrives. One entry per entry
  // into the mode, never one per step and never one per caret move — the same
  // reasoning DeckDetailPage's selection sentinel uses for `isAnySelected`
  // over the mutating `selection` object.
  //
  // This component only mounts on mobile (DeckDetailPage renders it in the
  // `!isDesktop` branch), so there is no `isDesktop` gate to repeat here.
  useHistorySentinel(activeCardId !== null, 'deckActiveCard', () => setActivation(null))

  // The row (label band + field) that currently holds the caret, together with
  // the card it belongs to — one target, not two: the card supplies the
  // geometry the rule is expressed in and the row says where inside it the
  // caret sits. Held as state rather than a ref because the §7 positioning
  // motion must happen AFTER the render that reserves scroll slack below the
  // feed, not during it.
  const [focusTarget, setFocusTarget] = useState<{ row: HTMLElement; card: HTMLElement | null } | null>(null)
  // Scroll room reserved below the feed while a field is focused. Sized to the
  // DEFICIT of the CURRENT focus target — the amount of scrolling the page
  // cannot otherwise perform because the content ends — and to nothing more
  // (ADR-067 Decision 4 as amended 2026-07-30). It still exists for the case it
  // was introduced for: the last field of the last card has no content beneath
  // it, so without the reserve `scrollTo` clamps at the end and the field stays
  // behind the keyboard. What it is no longer allowed to be is a session-held
  // maximum: that left enough slack for the page to be scrolled clean past the
  // field that was holding focus, which is the one thing it exists to prevent.
  // It is one more term in the single bottom-reserve sum below, never a second
  // source of padding.
  const [keyboardSlack, setKeyboardSlack] = useState(0)
  // Moving between two fields fires blur then focus. Clearing on the blur would
  // drop the slack for one frame and jog the page under the finger, so the
  // clear is deferred just long enough for an incoming focus to cancel it.
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const handleFieldFocus = useCallback((row: HTMLElement, card: HTMLElement | null) => {
    if (blurTimerRef.current) { clearTimeout(blurTimerRef.current); blurTimerRef.current = null }
    setFocusTarget((prev) => (prev && prev.row === row && prev.card === card ? prev : { row, card }))
  }, [])
  const handleFieldBlur = useCallback(() => {
    if (blurTimerRef.current) clearTimeout(blurTimerRef.current)
    blurTimerRef.current = setTimeout(() => { blurTimerRef.current = null; setFocusTarget(null) }, 80)
  }, [])
  useEffect(() => () => { if (blurTimerRef.current) clearTimeout(blurTimerRef.current) }, [])

  // §7 / ADR-067 Decision A (a57f293, sharpened by the amendment of the same
  // day) — the unit of focus positioning is the ACTIVE CARD, not the field, and
  // the placement is made in ONE eased motion. A card that fits the band is
  // brought fully inside it, after which a tap on any of its fields moves the
  // page by nothing at all. A card taller than the band places the focused
  // field so that BOTH its immediate neighbours stay on screen and tappable —
  // which is what makes a repeated tap just below (or just above) the focused
  // field walk the card one field per tap with no scrolling. That guarantee is
  // load-bearing rather than convenient: Enter is a line break in side-B fields
  // now, so a tap is the only route from one field of a card to the next, and a
  // neighbour left off screen has no route to it at all.
  //
  // This replaced the earlier least-movement target, which parked the focused
  // field against the band's lower clearance by construction and so put the
  // next field, the previous field and the card's own action row outside the
  // band. The assistance principle it was written under is untouched: the
  // placement happens once, at focus, and constrains nothing about where the
  // user may scroll afterwards, in either direction.
  //
  // `easedRowRef` records that the one eased motion for a row has been spent, so
  // a placement recomputed after the page has come to rest is instant rather
  // than a second visible movement. Revisions arriving while the motion is still
  // in flight are not a second motion and do not consult it — they re-aim the
  // running one, which is what makes the whole gesture read as one movement.
  const easedRowRef = useRef<HTMLElement | null>(null)
  // The reserve, mirrored into a ref. The motion below reads it every time it
  // recomputes a target, and it must read the value this gesture last WROTE,
  // not the value this render closed over — the two diverge precisely in the
  // window the placement runs in. Mirroring it is also what lets the positioning
  // effect depend on `focusTarget` ALONE: the reserve changing no longer
  // re-enters the effect, which is what used to turn one focus into a second,
  // competing settle pass.
  const slackRef = useRef(0)
  const applySlack = useCallback((next: number) => {
    slackRef.current = next
    setKeyboardSlack(next)
  }, [])
  useEffect(() => {
    if (!focusTarget) {
      easedRowRef.current = null
      if (slackRef.current !== 0) applySlack(0)
      return
    }
    const { row: focusRow, card: focusCard } = focusTarget
    const scroller = getAppScrollElement()
    if (!scroller) return

    // ONE motion per focus gesture, from focus until it comes to rest. Every
    // computation below aims this same motion; none of them starts a second one
    // and none of them cancels one in flight. That is the whole of this change:
    // the two writes the device probe recorded — an eased placement immediately
    // superseded by an instant caret-keeping write that truncated it — become
    // one motion whose target is revised.
    const motion = createScrollMotion(scroller)

    // 'placing' until the motion settles; 'placed' after. The distinction is
    // Decision A's: the card is positioned once, on activation, and typing is
    // not a repositioning event. So while placing, a late geometry change
    // re-runs the PLACEMENT (it is better-informed about where the card should
    // land); once placed, growth may only keep the caret in view and may never
    // drag the card back to where activation put it.
    let phase: 'placing' | 'placed' = 'placing'
    let needed = 0

    // The reserve is released only once the page has actually come to rest, and
    // only down to what the settled target needs — which by construction still
    // covers it, so the shrink cannot clamp the position just reached. This
    // replaces a 400 ms guess at the UA's smooth-scroll duration (§3.5): the
    // motion now reports its own arrival, so there is nothing left to guess.
    const settled = () => {
      phase = 'placed'
      if (needed < slackRef.current) applySlack(needed)
    }

    // `mode` separates the two things that can move the page while a field is
    // focused. 'position' is the placement rule above. 'caret' is the far
    // smaller job of keeping the caret visible as a field grows under typing.
    const aim = (mode: 'position' | 'caret', duration: number) => {
      if (!focusRow.isConnected) return
      const free = measureFreeSpace()
      if (!free) return
      // 'caret' asks the focused field where its caret actually is. Measured
      // here, at the correction, rather than tracked: it is a fact about the
      // instant the field finished growing, and this is that instant. An
      // unmeasurable caret returns null and the row's bottom stands in, which
      // is what this did for every caret before.
      const delta = mode === 'position'
        ? focusDelta(focusRow, focusCard, free)
        : caretKeepDelta(focusRow, free, focusedCaretKeepBottom(focusRow))

      // `scrollTop + delta` is an ABSOLUTE target, and stays correct when
      // recomputed mid-flight: the rects `focusDelta` reads have travelled with
      // the scroller, so the sum names the same landing position wherever the
      // motion currently is. This is what makes superseding a target safe.
      const target = scroller.scrollTop + delta

      // Scroll room the page has left beneath it, discounting the reserve
      // currently applied — so `needed` is an absolute size for the reserve
      // rather than an increment, and converges in one step whether the target
      // needs more than the last one or less.
      const base = scroller.scrollHeight - slackRef.current - scroller.clientHeight
      needed = Math.max(0, Math.ceil(target - base))
      // Growing the reserve moves nothing (padding is appended below the
      // content), so it is done inline and the motion carries straight on into
      // it: the loop clamps its writes to what the scroller can currently reach
      // and covers the remainder as soon as the padding renders. It no longer
      // costs a return and a second pass.
      if (needed > slackRef.current) applySlack(needed)

      if (mode === 'position') easedRowRef.current = focusRow
      motion.to(target, duration, settled)
    }

    const reduceMotion = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    // One eased motion per focus. A revision that arrives while the motion is
    // still running is that same motion re-aimed, so it keeps the ease (what is
    // left of it — `motion.to` preserves the remaining time rather than
    // restarting the clock). A placement computed after the page has already
    // come to rest is instant: the ease for this row has been spent, and easing
    // again would be the second visible step Decision A forbids.
    const easeFor = () => {
      if (motion.inFlight()) return PLACEMENT_DURATION
      if (reduceMotion || easedRowRef.current === focusRow) return 0
      return PLACEMENT_DURATION
    }
    const frame = requestAnimationFrame(() => aim('position', easeFor()))

    // ADR-067's amendment retires the scroll bound that used to live here:
    // focus positioning is an assistance offered once, not a constraint on
    // where the user may subsequently scroll. With focus held there is no
    // upward bound at all, and no bound is needed downward either — the
    // deficit-sized reserve above already makes the settled position the
    // page's own maximum scroll, so there is nothing below it to reach.
    //
    // The keyboard appearing, growing or going away changes the band the
    // placement was computed against, so the card is owed a fresh one — this is
    // the other event besides taking focus that Decision A treats as a
    // positioning event. When the keyboard is still animating in this fires
    // repeatedly against a band that is still moving; each one re-aims the one
    // motion rather than issuing a write of its own, so the last band measured
    // is the band the page finally settles against.
    const vv = window.visualViewport
    const correct = () => { phase = 'placing'; aim('position', easeFor()) }
    vv?.addEventListener('resize', correct)

    // The field growing is the other way the caret can fall out of the free
    // space. While the placement is still in flight the growth is part of what
    // the placement should have been computed against, so it revises the
    // placement. Once placed, it is corrected in 'caret' mode only: the card
    // stays where the author last left it, so a wrap adds a line without the
    // page jumping back. The observer's initial delivery is skipped — it lands
    // in the same frame as the placement and carries no new information.
    let observer: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined') {
      let primed = false
      observer = new ResizeObserver(() => {
        if (!primed) { primed = true; return }
        if (phase === 'placing') aim('position', easeFor())
        else aim('caret', 0)
      })
      observer.observe(focusRow)
    }

    // The placement governs nothing once the author takes over. A deliberate
    // scroll gesture ends the motion where it stands and demotes the gesture to
    // 'placed', so no later growth re-places the card under a finger. This is
    // the assistance principle, not a lock: nothing here constrains where the
    // page may go, it only stops us writing while the user is writing.
    // `touchmove`/`wheel` rather than `touchstart`, because the tap that took
    // focus in the first place must not abort its own placement.
    const yieldToUser = () => { if (motion.inFlight()) { motion.abort(); phase = 'placed' } }
    scroller.addEventListener('touchmove', yieldToUser, { passive: true })
    scroller.addEventListener('wheel', yieldToUser, { passive: true })

    return () => {
      cancelAnimationFrame(frame)
      motion.abort()
      vv?.removeEventListener('resize', correct)
      scroller.removeEventListener('touchmove', yieldToUser)
      scroller.removeEventListener('wheel', yieldToUser)
      observer?.disconnect()
    }
  }, [focusTarget, applySlack])

  // Field deletion's transient Undo (§12) and the active card's save errors
  // share one slot above the editing bar: both are momentary reports about the
  // card being edited, and two stacked strips over a raised keyboard would eat
  // the free space the focused field needs.
  const [fieldUndo, setFieldUndo] = useState<{ label: string; restore: () => void } | null>(null)
  const [activeSaveError, setActiveSaveError] = useState<string | null>(null)
  // The third report the slot carries: a reorder handed a repeated kind's
  // authority to another field, so Learn now prompts from a different one
  // (ADR-066 Decision 1). It belongs here for the same reason the other two do
  // — it is a momentary statement about the card being edited — and it belongs
  // here rather than nowhere because the alternative is the author discovering
  // the change during study, days later, with nothing on screen connecting it
  // to the drag that caused it.
  const [fieldOrderNotice, setFieldOrderNotice] = useState<string | null>(null)
  const transientTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  function clearTransient() {
    if (transientTimerRef.current) clearTimeout(transientTimerRef.current)
    transientTimerRef.current = null
    setFieldUndo(null)
    setActiveSaveError(null)
    setFieldOrderNotice(null)
  }
  // The Undo closure reaches into the active card's own draft, so it stops
  // being meaningful the moment a different card becomes active.
  useEffect(() => { clearTransient(); return clearTransient }, [activeCardId])

  function showFieldUndo(info: { label: string; restore: () => void }) {
    clearTransient()
    setFieldUndo(info)
    transientTimerRef.current = setTimeout(() => { transientTimerRef.current = null; setFieldUndo(null) }, 8000)
  }
  function showActiveSaveError(message: string) {
    clearTransient()
    setActiveSaveError(message)
    transientTimerRef.current = setTimeout(() => { transientTimerRef.current = null; setActiveSaveError(null) }, 6000)
  }
  // No Undo control on this one, and the omission is deliberate: the reorder is
  // reversed by dragging the field back, which is the same gesture the author
  // just made and is available for as long as the card is active — not for the
  // few seconds a transient button would live. The strip states what changed;
  // it does not need to also be the way back.
  const handleFieldOrderChanged = useCallback((labels: string[]) => {
    clearTransient()
    setFieldOrderNotice(cb.fieldPromptChangedMsg(labels.join(', ')))
    transientTimerRef.current = setTimeout(() => { transientTimerRef.current = null; setFieldOrderNotice(null) }, 7000)
  }, [cb])

  // + Field (brief §11) — the chooser and the transient strip share the same
  // fixed slot above the editing bar, so opening one dismisses the other.
  function handleToggleFieldChooser() {
    if (!activeCardId) return
    setFieldChooserOpen((prev) => {
      const next = !prev
      if (next) clearTransient()
      return next
    })
  }
  function handleAddField(kind: string, label?: string) {
    activeCardHandleRef.current?.addField(kind, label)
    setFieldChooserOpen(false)
  }

  // ── Previous / Next and the counter (brief §13) ──────────────────────────
  // The type of field the walk is CARRYING — not simply the type last focused.
  // The two differ in exactly one case, and it is the case the whole feature
  // turns on: landing on a card that lacks the type falls back to the Term, and
  // if that fallback were allowed to become the carried type, the next press
  // would walk Terms instead of resuming the Translations the author was
  // correcting. So the fallback's own focus report is skipped, and every other
  // report — a tap on another field, a field created by `+ Field` — updates it.
  const walkTypeRef = useRef<FieldType>(TERM_FIELD_TYPE)
  const skipNextTypeReportRef = useRef(false)
  const handleFocusedFieldType = useCallback((type: FieldType) => {
    if (skipNextTypeReportRef.current) { skipNextTypeReportRef.current = false; return }
    walkTypeRef.current = type
    // A real field took the caret, so the affordance offering to create one has
    // done its job (or been passed over deliberately) and stops rendering.
    setActivation((prev) => (prev?.missingType ? { ...prev, missingType: undefined } : prev))
  }, [])

  // Card id → row index, built once per feed. The per-row `findIndex` this
  // replaces made rendering O(n²) — brief §21.3 item 4.
  const indexById = useMemo(() => buildCardIndexMap(feedCards), [feedCards])

  // Position in the SHOWN set. `null` covers both "nothing active" and "the
  // unsaved new card is active" — the draft has no place in the shown set, so
  // §4.2's dash form and its first-card behaviour are right for it too.
  const activeIndex = useMemo(() => {
    if (!activeCardId || activeCardId === DRAFT_CARD_ID) return null
    return indexById.get(activeCardId) ?? null
  }, [indexById, activeCardId])

  // Where the NEXT arrow press counts from. Distinct from `activeIndex`, which
  // is state and therefore one render behind: five quick presses of `›` must
  // walk five cards, and reading state would make presses two-to-five all
  // compute the same target from the same stale index and walk one. The cursor
  // is written synchronously by `goToCard` and re-synced to whatever actually
  // became active, so a tap on some other card retargets the walk correctly.
  const navCursorRef = useRef<string | null>(null)
  useEffect(() => { navCursorRef.current = activeCardId }, [activeCardId])

  /**
   * The number a row wears — its position in the DECK, not in the window.
   *
   * These are two different questions and both surfaces used to answer the
   * window's. Under a search the server hands back each hit's deck ordinal;
   * under an anchored window it is the anchor's offset plus the row. Getting
   * this wrong is the same class of untruth as the loaded-set counter: a card
   * labelled `3` that is the 803rd in the deck.
   */
  const deckNumberOf = useCallback((rowIndex: number): number => {
    if (searching) {
      const hit = searchResults[rowIndex]
      return (hit ? hit.index : rowIndex) + 1
    }
    return (anchorPage - 1) * pageSize + rowIndex + 1
  }, [searching, searchResults, anchorPage, pageSize])

  const grammar = counterGrammar({ searching, filtered: chip !== 'all' })
  // `searchTotal` is the server's deck-wide match count, so `FOUND` now counts
  // the deck rather than the rows that happened to be loaded — the arrival
  // `counterTotal` was written to expect.
  const counterDenominator = counterTotal(grammar, deckCardCount, feedCards.length, searchTotal)

  /** Activates `target`, carrying the walk's field type onto it (§13). */
  const goToCard = useCallback((target: Card) => {
    setFieldChooserOpen(false)
    navCursorRef.current = target.id
    const type = walkTypeRef.current
    const key = findFieldKeyOfType(target, type)
    // No field of that type: the caret still has to land somewhere, so it takes
    // the Term, and the card shows the affordance that creates the missing one.
    skipNextTypeReportRef.current = key === null
    setActivation({
      cardId: target.id,
      fieldKey: key ?? TERM_FIELD_KEY,
      caret: null,
      missingType: key === null ? type : undefined,
    })
    // Nothing here scrolls. The target card's own field takes focus on mount and
    // reports it through `onFieldFocus`, which aims the single placement motion
    // — the same path a tap takes. A scroll issued from here would be the second
    // writer ADR-067 Decision D forbids, and rapid arrow presses would queue
    // conflicting placements instead of the latest target simply winning.
  }, [])

  const step = useCallback((direction: -1 | 1) => {
    if (!isOwner || !editingMode || selectMode) return
    const cursor = navCursorRef.current
    const from = cursor && cursor !== DRAFT_CARD_ID
      ? indexById.get(cursor) ?? null
      : null
    const target = stepTarget(from, direction, feedCards.length)
    if (target === null) return
    goToCard(feedCards[target])
  }, [isOwner, editingMode, selectMode, feedCards, indexById, goToCard])

  // What the active card renders in the missing field's place, if anything.
  // The Term is never "missing" — every card has one — so a walk carrying it
  // can never produce this.
  const missingFieldDescriptor = useMemo(() => {
    const type = activation?.missingType
    if (!type || type.kind === 'term') return null
    return {
      kind: type.kind,
      // Only the custom (`note`) type carries an authored label; every other
      // kind takes its fixed STANDARD_LABEL inside `addField`, exactly as the
      // chooser's own calls do.
      label: type.customName ?? undefined,
      displayName: fieldTypeLabel(type, ce),
    }
  }, [activation?.missingType, ce])

  // Quick jump by number (§13).
  //
  // A jump names a 1-based position in the DECK, and the feed holds only the
  // window starting at `anchorPage`, so the target is routinely not loaded.
  // This used to be answered by pulling page after page until the count caught
  // up — 35 requests and ~19 s of blank screen on a 1029-card deck (§21.3). The
  // ordinal is arithmetic, not a search: page `(n-1) / pageSize | 0` + 1 holds
  // card `n` by construction, so re-anchoring there is ONE request, and the
  // card is in the first page that arrives.
  const [jumpOpen, setJumpOpen] = useState(false)
  const [jumpValue, setJumpValue] = useState('')
  // A jump whose page is on its way. Held only until the target row exists —
  // the wait is one request, not a chain, so nothing here fetches.
  const [pendingJump, setPendingJump] = useState<number | null>(null)

  /** Row index within the currently loaded window for deck position `n`
   * (1-based), or null when that position lies outside the window. */
  const windowIndexOf = useCallback((n: number): number | null => {
    const i = n - 1 - (anchorPage - 1) * pageSize
    return i >= 0 && i < feedCards.length ? i : null
  }, [anchorPage, pageSize, feedCards.length])

  useEffect(() => {
    if (pendingJump === null) return
    const i = windowIndexOf(pendingJump)
    if (i !== null) {
      goToCard(feedCards[i])
      setPendingJump(null)
      return
    }
    // The window moved but does not contain the target — the deck shrank under
    // the jump, or the anchor was reset. Land on the last row rather than
    // waiting for a page that is not coming.
    if (!hasNextPage && !isFetchingNextPage && feedCards.length > 0) {
      goToCard(feedCards[feedCards.length - 1])
      setPendingJump(null)
    }
  }, [pendingJump, feedCards, windowIndexOf, hasNextPage, isFetchingNextPage, goToCard])

  useEffect(() => { if (!editingMode || selectMode) { setJumpOpen(false); setPendingJump(null) } }, [editingMode, selectMode])

  function submitJump() {
    const n = Number.parseInt(jumpValue, 10)
    if (!Number.isFinite(n)) return
    const clamped = Math.min(Math.max(n, 1), Math.max(counterDenominator, 1))
    setJumpOpen(false)
    setJumpValue('')

    // Inside a search the number addresses the FOUND set, which is already fully
    // addressable by the arrows and needs no page arithmetic.
    if (searching) {
      if (clamped <= feedCards.length) goToCard(feedCards[clamped - 1])
      return
    }

    const loaded = windowIndexOf(clamped)
    if (loaded !== null) { goToCard(feedCards[loaded]); return }

    onAnchorChange(Math.floor((clamped - 1) / pageSize) + 1)
    setPendingJump(clamped)
  }

  // ── The deep link's arrival (Prompt 19) ──────────────────────────────────
  //
  // A `?card=` link is the Quick Lookup sheet's exit, and the sheet is the
  // phone's own entry point, so this is the ordinary way a reader reaches a
  // card here. The desktop panels have arrived this way since implementation
  // item 5; this is the same arrival, made where the phone's rows are.
  //
  // Deliberately NOT routed through `goToCard` / `pendingJump` above, although
  // the shape is theirs. That machinery activates a card: it is gated on
  // editing mode and ownership, it takes the caret, and the only scroll it
  // produces is the consequence of placing that caret — which raises the
  // keyboard on arrival from a lookup sheet and does nothing at all for a
  // reader who does not own the deck. Arriving is a viewing-mode notion: move
  // to the row, mark it, touch nothing else.
  //
  // What IS borrowed from the jump is its patience and its limit: the target is
  // routinely not loaded when the link is followed, so this waits for the
  // anchored page and the row it carries, and stops waiting once the window has
  // settled without it and nothing further is in flight.

  /** Row elements, keyed by card id, written by the render that creates them
   * (the feed's own row map below). ADR-067 Decision 6 forbids scroll-to-card
   * by node lookup: the index says which card, the render hands over the
   * element — the same way it hands the focus placement its row and card
   * through `onFieldFocus` rather than being queried for them. */
  const rowElsRef = useRef<Map<string, HTMLElement>>(new Map())
  /** Spent once the arrival has been made, so a re-render does not repeat it.
   * Reset by the link naming a new target, or the same one again. */
  const arrivedRef = useRef(false)
  /** The arrival's motion, so a second arrival supersedes the first rather than
   * running beside it. One writer to the scroller, always. */
  const arrivalMotionRef = useRef<ScrollMotion | null>(null)
  /**
   * What the arrival had to say for itself, if anything: that it turned a
   * narrowing off to reach the card, or that it could not find the card at all.
   * Both are momentary sentences in the same register as the anchor notice
   * below — the screen states what it did rather than leaving the reader to
   * infer it from a filter that is suddenly off, or from a page with nothing
   * marked on it.
   */
  const [arrivalNotice, setArrivalNotice] = useState<'narrowing-cleared' | 'not-found' | null>(null)

  useEffect(() => {
    arrivedRef.current = false
    setArrivalNotice(null)
  }, [deepLinkCardId, deepLinkNonce])

  useEffect(() => {
    if (!deepLinkCardId || arrivedRef.current) return

    // Is the target in the DATA, at the index the page resolved? A miss means
    // the located page has not landed yet — the same wait the jump makes, and
    // the same give-up when there is nothing left to wait for.
    if (deepLinkWindowIndex === null || cards[deepLinkWindowIndex]?.id !== deepLinkCardId) {
      if (deepLinkPending) return
      arrivedRef.current = true
      setArrivalNotice('not-found')
      return
    }

    const row = rowElsRef.current.get(deepLinkCardId)
    if (!row?.isConnected) {
      // In the window, but no row on screen. Either something is narrowing the
      // feed — clear it and say so, which is what §15 already does when opening
      // a search drops a chip — or the row simply has not mounted yet.
      if (chip !== 'all' || searching) {
        if (chip !== 'all') setChip('all')
        if (searching) { setQuery(''); onCloseSearch() }
        // The chip is not handed back afterwards: search borrowed it, this
        // arrival turned it off outright, and restoring it would hide the card
        // again a render later.
        setChipBeforeSearch(null)
        setArrivalNotice('narrowing-cleared')
        // Guard left unconsumed on purpose — the row exists on the next render.
        return
      }
      // The saved copy of a card being created is hidden behind its own draft
      // row; that row goes away on its own, so this is still a wait.
      if (draft?.savedId === deepLinkCardId) return
      if (deepLinkPending) return
      arrivedRef.current = true
      setArrivalNotice('not-found')
      return
    }

    const scroller = getAppScrollElement()
    if (!scroller) return

    arrivedRef.current = true

    const rowRect = row.getBoundingClientRect()
    const viewRect = scroller.getBoundingClientRect()
    // Centred in the scrollport. Read from live rects, never derived from the
    // index: the feed is not virtualised (Decision 6), so there is no row-height
    // model to derive an offset from, and building one is exactly what that
    // decision declines to do.
    const delta = (rowRect.top + rowRect.height / 2) - (viewRect.top + viewRect.height / 2)
    const reduceMotion = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches

    // `createScrollMotion` — the same single-writer, retargetable ease this
    // surface's focus placement uses. It wraps no scroll primitive and holds no
    // lock: the reader's own touch or wheel ends it where it stands (the
    // assistance principle of ADR-067's 2026-07-30 amendment).
    arrivalMotionRef.current?.abort()
    const motion = createScrollMotion(scroller)
    arrivalMotionRef.current = motion
    motion.to(Math.max(0, scroller.scrollTop + delta), reduceMotion ? 0 : PLACEMENT_DURATION)

    const yieldToUser = () => { if (motion.inFlight()) motion.abort() }
    scroller.addEventListener('touchmove', yieldToUser, { passive: true })
    scroller.addEventListener('wheel', yieldToUser, { passive: true })

    // The mark is the page's: one mark, one expiry, both layouts.
    onDeepLinkArrived(deepLinkCardId)

    return () => {
      scroller.removeEventListener('touchmove', yieldToUser)
      scroller.removeEventListener('wheel', yieldToUser)
    }
    // `feedCards` is named although the body never reads it: it is the set the
    // render below turns into rows, so it changing is the signal that the row
    // registry has changed under us — including the render that follows a
    // narrowing being cleared.
  }, [
    deepLinkCardId, deepLinkNonce, deepLinkWindowIndex, deepLinkPending,
    cards, feedCards, chip, searching, draft?.savedId, onCloseSearch, onDeepLinkArrived,
  ])

  // The notice says what just happened, not what is permanently true, so it
  // goes away on its own — the same lifetime the transient strip's own reports
  // have.
  useEffect(() => {
    if (!arrivalNotice) return
    const timer = setTimeout(() => setArrivalNotice(null), 6000)
    return () => clearTimeout(timer)
  }, [arrivalNotice])

  // Leaving the screen mid-motion: the scroller outlives this feed, so the loop
  // has to be stopped rather than left writing to it.
  useEffect(() => () => { arrivalMotionRef.current?.abort() }, [])

  /** §15's "search everywhere" — hands the typed word to the global palette,
   * which is where translating, speaking and adding live. */
  const openGlobalSearch = useLookupPaletteStore((s) => s.openWithQuery)
  const handleSearchEverywhere = useCallback(() => {
    openGlobalSearch(trimmedQuery)
  }, [openGlobalSearch, trimmedQuery])

  // + Card (brief §10). Appends, activates, focuses Term, scrolls to it — and
  // from the viewing bar it enters editing mode too, so one press is one
  // intent. An edit in progress on another card is committed on the way: the
  // previously active row unmounts, and its unmount commit is the flush.
  function handleAddCard(seedTerm = '') {
    if (!isOwner) return
    setFieldChooserOpen(false)
    if (!editingMode) onEnterEditing()
    const key = `draft-${Date.now()}`
    setDraft({ key, savedId: null, term: seedTerm })
    setActivation({ cardId: DRAFT_CARD_ID, fieldKey: TERM_FIELD_KEY, caret: null })
    // The card lands at the end of the feed, so following it is the existing
    // scroll-to-end path. Decision A's card placement then refines the position
    // when Term takes focus — reserving whatever deficit the last card of the
    // deck has behind the keyboard. This only guarantees the new card is on
    // screen and that the sticky-bottom latch does not fight it.
    wasNearBottomRef.current = false
    requestAnimationFrame(() => scrollToBottom('smooth'))
  }

  // The feed no longer scrolls inside itself — the page is the only scrolling
  // surface (ADR-067 Decision 3), so "scroll to newest" and the near-bottom
  // test both read Layout's <main> instead of a container of our own.
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
    const el = getAppScrollElement()
    if (!el) return
    el.scrollTo({ top: el.scrollHeight, behavior })
  }, [])

  const measureNearBottom = useCallback((): boolean | null => {
    const el = getAppScrollElement()
    if (!el) return null
    const overflow = el.scrollHeight - el.clientHeight
    // A page that doesn't overflow is always "at the bottom" — otherwise a
    // short deck would permanently show the jump-to-latest affordance with
    // nowhere to jump to.
    return overflow <= 0 || overflow - el.scrollTop < 60
  }, [])

  // The only thing that may GRANT stickiness: the reader actually scrolled
  // there. Everything else may revoke it but never confer it.
  const handleScroll = useCallback(() => {
    const nearBottom = measureNearBottom()
    if (nearBottom === null) return
    wasNearBottomRef.current = nearBottom
    setShowJumpToLatest(!nearBottom)
  }, [measureNearBottom])

  useEffect(() => {
    const el = getAppScrollElement()
    if (!el) return
    el.addEventListener('scroll', handleScroll, { passive: true })
    return () => el.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  // Re-measures the jump affordance against a new layout. It may only revoke
  // stickiness, never grant it: while the loading skeleton is on screen there
  // is no overflow, which measures as "at the bottom", and conferring on that
  // basis is precisely the defect above — the flag was then still set when the
  // first page landed a commit later.
  const refreshJumpAffordance = useCallback(() => {
    const nearBottom = measureNearBottom()
    if (nearBottom === null) return
    setShowJumpToLatest(!nearBottom)
    if (!nearBottom) wasNearBottomRef.current = false
  }, [measureNearBottom])

  // Latched while a next-page request is in flight and consumed when that
  // page's cards land. A state latch, not a timer: it is set and cleared by
  // observable transitions rather than by guessing when loading finished.
  const grownByPaginationRef = useRef(false)
  useEffect(() => {
    if (isFetchingNextPage) grownByPaginationRef.current = true
  }, [isFetchingNextPage])

  // A change in card count has two very different causes and they must not be
  // conflated:
  //   • a page of further cards arriving — never moves the view. Yanking to
  //     the end would skip past the cards just fetched and re-expose the
  //     sentinel, which is the chain this fixes.
  //   • a card the reader just created — followed to the bottom, but only if
  //     they were genuinely there when it landed.
  useEffect(() => {
    const byPagination = grownByPaginationRef.current
    grownByPaginationRef.current = false
    if (!byPagination && wasNearBottomRef.current) scrollToBottom()
    refreshJumpAffordance()
  }, [displayedCards.length, refreshJumpAffordance, scrollToBottom])
  // The sentinel pages whichever list is on screen. While searching that is the
  // MATCH list, not the deck: pulling deck pages under a search would grow a
  // set the feed is not showing and leave the reader at the end of the matches
  // with nothing new, while the real remaining matches stayed unfetched.
  const moreToLoad = searching ? search.hasNextPage : hasNextPage
  const loadingMore = searching ? search.isFetchingNextPage : isFetchingNextPage
  const loadMore = searching ? search.fetchNextPage : fetchNextPage
  useEffect(() => {
    const el = sentinelRef.current
    if (!el || !moreToLoad) return
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting && !loadingMore) loadMore() },
      { threshold: 0.1 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [moreToLoad, loadingMore, loadMore])

  // ── The two Fill flows ───────────────────────────────────────────────────
  //
  // Both live in `useDeckFillFlows` rather than here: the desktop deck screen
  // reaches the same two flows from its own ⋯ menu and selection bar, and a
  // phone component's internals were not reachable from there. See that module
  // for why a second implementation of the cost disclosure was not an option.
  const fillAll = useFillAllFlow(deckId, fillAllOpen, onCloseFillAll)
  // `Select all` means the deck (item 9), so a selection's Fill can be as large
  // as Fill-all itself and gets the same preflight sheet rather than starting
  // immediately the way a single card's Fill still does (§5.2 — a lone direct
  // action needs none of this).
  const fillSelection = useFillSelectionFlow(deckId, selection, setSelection)

  function handleFillSelected() {
    if (!selectMode) return
    fillSelection.open()
  }

  function toggleSelect(id: string) {
    setSelection((prev) => toggleCardSelected(prev, id))
  }
  function selectCard(id: string) {
    setSelection((prev) => setCardSelected(prev, id, true))
  }
  /**
   * `Select all` — the deck, expressed as a scope the server resolves.
   *
   * It carries the active chip, so "Not filled" + Select all is every
   * not-filled card in the deck rather than the not-filled cards among the
   * pages fetched so far.
   *
   * Unavailable while a search is open, and it says why. A search narrows to
   * matches over the whole deck, but the selection type has no way to name that
   * set, and selecting the matches currently on screen would be this very
   * defect wearing a fix's clothes. Picking search results one by one still
   * works — those ids are real.
   */
  function selectAllInDeck() {
    if (searching) return
    setSelection(selectWholeDeck(chip))
  }
  function exitSelectMode() {
    setSelection(EMPTY_SELECTION)
  }

  // A chip moving under an active whole-deck selection rebases it: the old
  // exclusions were about a different set, so keeping them would make the
  // count a guess.
  useEffect(() => {
    setSelection((prev) => selectionFilterChanged(prev, chip))
  }, [chip, setSelection])

  // Every pinned element here is `position: fixed`, not `sticky` — see
  // docs/diagnostics/composer-keyboard-positioning-measurement-2026-07-26.md
  // §1–2. Sticky pinned them to <main>'s scrollport, capped by <main>'s own
  // containing block, which is exactly why they used to rest mid-screen on a
  // short deck regardless of the keyboard. `fixed` resolves against the
  // layout viewport unconditionally, so `bottomGap` (the keyboard/strip band
  // `visualViewport` isn't reflecting into the layout viewport, 0 on
  // well-behaved platforms) can be applied directly with nothing to
  // double-compensate.
  const bottomGap = useViewportBottomGap()
  // The one offset every pinned element on this screen shares. No nav height is
  // reserved: the global bottom navigation does not render inside a deck at all
  // (ADR-067 Decision 1), so a bar sits on the true bottom edge of the viewport.
  // With the keyboard raised `bottomGap` is the band the keyboard occupies,
  // which is what glues the bar to it; the safe-area inset is added only when
  // the keyboard is down, since the keyboard covers it.
  //
  // This used to be conditioned on whether the composer's input held focus.
  // With the composer gone the condition has no subject: the bars carry no text
  // input, so nothing on this screen raises the keyboard except a card's own
  // field, and `bottomGap` already reports that band.
  const pinnedBottom = `calc(${bottomGap}px + env(safe-area-inset-bottom, 0px))`

  // Height only — each of these reserves space at the end of the feed so the
  // last card clears the pinned element it sits behind. Attachment follows the
  // element into the DOM (see the hook), so it is correct even though the feed
  // renders a loading skeleton before either element exists.
  const [selectBarRef, selectBarHeight] = useMeasuredHeight()
  const [viewBarRef, viewBarHeight] = useMeasuredHeight()
  const [editBarRef, editBarHeight] = useMeasuredHeight()
  // The + Field chooser and the Undo/save-error strip share one fixed slot
  // above the editing bar and are mutually exclusive (never both mounted at
  // once), so one measurement covers both — same convention as every other
  // pinned element's reserve, not a second mechanism alongside it.
  const [floatingStripRef, floatingStripHeight] = useMeasuredHeight()

  // The viewing-mode bar (ADR-067 Decision 1). It takes the bottom edge
  // directly. It used to be stacked on top of the composer's measured height,
  // because the composer held that edge until `+ Card` could replace it (brief
  // §25: the composer lives until item 3). Item 3 is now closed, the composer
  // is gone, and the stacking term went with it rather than being left as a
  // `+ 0px` that no longer names anything.
  const viewBar = !selectMode && !editingMode && (
    <div
      ref={viewBarRef}
      {...PINNED_PROBE}
      style={{ position: 'fixed', left: 0, right: 0, bottom: pinnedBottom, zIndex: 21 }}
    >
      <DeckViewBar
        onStudy={onStudy}
        // The one place the bar's write actions are offered or withheld, from
        // `isOwner` (isDeckOwner — which already excludes system decks). Both
        // go together: a deck the reader cannot write to gets Study alone.
        write={isOwner ? { onAddCard: () => handleAddCard(), onEnterEditing } : undefined}
      />
    </div>
  )

  // Editing mode's bar (ADR-067 Decision 2) — the viewing bar's replacement in
  // the same slot, never stacked with it (the two guards are mutually
  // exclusive on `editingMode`). Same `fixed` + measured-height convention as
  // every other pinned element here.
  const editBar = !selectMode && editingMode && (
    <div
      ref={editBarRef}
      {...PINNED_PROBE}
      style={{ position: 'fixed', left: 0, right: 0, bottom: pinnedBottom, zIndex: 21 }}
    >
      <DeckEditBar
        onDone={onExitEditing}
        onAddCard={() => handleAddCard()}
        onPrev={() => step(-1)}
        onNext={() => step(1)}
        canPrev={stepTarget(activeIndex, -1, feedCards.length) !== null}
        canNext={stepTarget(activeIndex, 1, feedCards.length) !== null}
        // `SHOWN` and `FOUND` count the narrowed set, so the numerator is the
        // row's place in it; `plain` counts the deck, so the numerator must be
        // the deck ordinal — otherwise an anchored window would read `1 OF 150`
        // while sitting on card 803.
        position={activeIndex === null
          ? null
          : grammar === 'plain' ? deckNumberOf(activeIndex) : activeIndex + 1}
        total={counterDenominator}
        grammar={grammar}
        onOpenJump={() => { setFieldChooserOpen(false); setJumpOpen((o) => !o) }}
        onClearSearch={() => { setQuery(''); onCloseSearch() }}
      />
    </div>
  )

  // Quick jump (§13) — the number-input form. Shares the floating slot above
  // the editing bar with the chooser and the transient strip, and is mutually
  // exclusive with both: three stacked strips over a raised keyboard is exactly
  // what that one slot exists to prevent.
  const jumpStrip = jumpOpen && editingMode && !selectMode && !fieldChooserOpen && (
    <div
      ref={floatingStripRef}
      {...PINNED_PROBE}
      style={{
        position: 'fixed', left: 10, right: 10, zIndex: 22,
        bottom: `calc(${pinnedBottom} + ${editBarHeight + 10}px)`,
        display: 'flex', alignItems: 'center', gap: 8,
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-float)',
        padding: '10px 12px',
      }}
    >
      <span style={{ fontFamily: 'var(--font-ui)', fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary)' }}>
        {dd.navJumpTitle}
      </span>
      <input
        autoFocus
        inputMode="numeric"
        value={jumpValue}
        onChange={(e) => setJumpValue(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
        onKeyDown={(e) => { if (e.key === 'Enter') submitJump() }}
        placeholder={dd.navJumpPlaceholder}
        style={{
          flex: 1, minWidth: 0, height: 36, padding: '0 10px',
          background: 'var(--bg-page)', color: 'var(--text-primary)',
          border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
          fontFamily: 'var(--font-ui)', fontSize: 14,
        }}
      />
      {pendingJump !== null ? (
        <span style={{ fontFamily: 'var(--font-ui)', fontSize: 11.5, color: 'var(--text-secondary)' }}>
          {dd.navJumpLoading}
        </span>
      ) : (
        <button onClick={submitJump} style={jumpGoStyle}>{dd.navJumpGo}</button>
      )}
      <button
        onClick={() => { setJumpOpen(false); setJumpValue('') }}
        aria-label={dd.navJumpCloseAria}
        style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: 4 }}
      >
        <X size={15} strokeWidth={2.2} />
      </button>
    </div>
  )

  // + Field's type chooser (brief §11) — same fixed slot as the transient
  // strip below, and mutually exclusive with it (handleToggleFieldChooser
  // clears the strip on open; a save error or Undo arriving while the chooser
  // is open would otherwise stack two floating strips over the keyboard).
  const fieldChooser = fieldChooserOpen && editingMode && !selectMode && (
    <div
      ref={floatingStripRef}
      {...PINNED_PROBE}
      style={{
        position: 'fixed', left: 10, right: 10, zIndex: 22,
        bottom: `calc(${pinnedBottom} + ${editBarHeight + 10}px)`,
      }}
    >
      <FieldTypeChooser onSelect={handleAddField} onClose={() => setFieldChooserOpen(false)} />
    </div>
  )

  // Rides directly above the editing bar, so it stays visible with the keyboard
  // raised (the bar's own offset already accounts for it) instead of being
  // pinned to a bottom edge the keyboard has covered. No modal confirmation
  // anywhere in field deletion (§12) — this strip IS the confirmation, after
  // the fact and reversible.
  const transientStrip = (fieldUndo || activeSaveError || fieldOrderNotice) && editingMode && !selectMode && !fieldChooserOpen && !jumpOpen && (
    <div
      ref={floatingStripRef}
      {...PINNED_PROBE}
      style={{
        position: 'fixed', left: 10, right: 10, zIndex: 22,
        bottom: `calc(${pinnedBottom} + ${editBarHeight + 10}px)`,
        display: 'flex', alignItems: 'center', gap: 10,
        background: 'var(--control-solid)', color: 'var(--control-solid-text)',
        borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-float)',
        padding: '10px 10px 10px 14px',
      }}
    >
      {/* Not tinted with --danger: the strip's own plane inverts between
          themes (--control-solid is near-black in light, near-white in dark),
          so a fixed accent colour would land on one of them with too little
          contrast. The wording carries the failure; the colour carries nothing. */}
      <span style={{ flex: 1, minWidth: 0, fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 500 }}>
        {activeSaveError ?? fieldOrderNotice ?? (fieldUndo ? cb.fieldDeletedMsg(fieldUndo.label) : '')}
      </span>
      {fieldUndo && !activeSaveError && (
        <button
          onClick={() => { const { restore } = fieldUndo; clearTransient(); restore() }}
          style={{
            flexShrink: 0, minHeight: 36, padding: '0 14px',
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'var(--control-solid-text)',
            fontFamily: 'var(--font-ui)', fontSize: 13.5, fontWeight: 700,
          }}
        >
          {cb.undoBtn}
        </button>
      )}
    </div>
  )

  // What the arrival had to do to show the card, or that it could not show it
  // at all. Plain register, like the anchor notice in the feed — a sentence
  // about how the reader came to be looking at this, not a warning.
  //
  // FIXED under the top strip rather than in the feed's flow, which is where it
  // first sat: the whole point of an arrival is that it leaves the reader
  // somewhere other than the top of the list, so a line printed there is a line
  // said several thousand pixels away from the person it is addressed to. This
  // is the only viewing-mode element in that band (the search panel owns it,
  // and search is closed by the time this can show), so it collides with
  // nothing — unlike the bottom slot, where the jump-to-latest pill sits after
  // exactly the kind of scroll an arrival performs.
  //
  // `role="alert"` for the not-found case, which is the only thing on screen
  // reporting that a link the reader followed led nowhere.
  const arrivalStrip = !selectMode && arrivalNotice && (
    <div
      role="alert"
      style={{
        position: 'fixed', left: 10, right: 10, zIndex: 20,
        top: `calc(${DECK_TOP_STRIP_CLEARANCE} + 8px)`,
        display: 'flex', alignItems: 'center',
        padding: '9px 13px',
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)',
        boxShadow: 'var(--shadow-float)',
        fontFamily: 'var(--font-ui)', fontSize: 12.5, color: 'var(--text-secondary)',
      }}
    >
      {arrivalNotice === 'narrowing-cleared'
        ? tr.deckManagement.deepLinkNarrowingCleared
        : tr.deckManagement.deepLinkNotFound}
    </div>
  )

  // Same treatment as the other bars, for the same reason: `sticky` anchored it
  // to <main>'s scrollport capped by its containing block, so it rested
  // mid-screen on a short deck. `fixed` + the one viewport measurement puts it
  // on the real bottom edge.
  const selectBar = isOwner && selectMode && (
    <div
      ref={selectBarRef}
      {...PINNED_PROBE}
      style={{ position: 'fixed', left: 0, right: 0, bottom: pinnedBottom, zIndex: 21 }}
    >
      <SelectModeActionBar
        selectedCount={selectedCount}
        totalSelectable={totalSelectable}
        onExit={exitSelectMode}
        onSelectAll={selectAllInDeck}
        onFill={handleFillSelected}
        onMove={onRequestMove}
        onCopy={onRequestCopy}
        onDelete={onRequestDelete}
        fillingProgress={fillSelection.progress}
        resolving={fillSelection.resolving}
        // Both notices refuse rather than proceed on part of the deck: one when
        // the deck's size could not be read at all, one when Fill could not
        // resolve the selection.
        notice={fillSelection.error ?? (countsUnavailable ? dd.countUnavailable : null)}
      />
    </div>
  )

  if (cardsLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {[0, 1, 2].map((i) => (
          <div key={i} style={{ height: 64, borderRadius: 'var(--radius-md)', background: 'var(--bg-secondary)', opacity: 1 - i * 0.2 }} />
        ))}
      </div>
    )
  }

  // The empty deck (brief §22): "два равноправных входа: набрать первую
  // карточку или импортировать текст, из которого пришли слова."
  //
  // Two entrances, neither a duplicate of the other, and both equal in weight —
  // so neither is styled as the primary. What they replaced:
  //   • "Add cards one by one" opened a separate bulk-create screen. It is now
  //     `handleAddCard`, the very call `+ Card` makes, so the first card is
  //     created exactly where every later card is and the empty deck is not a
  //     second creation path to keep in agreement with the first.
  //   • "Import CSV" is gone from here. §25 records it as never specified for
  //     this surface, and the entrance the brief does specify is the *text* the
  //     words came from, not a spreadsheet. CSV import is untouched and keeps
  //     its home in the deck's overflow menu.
  //
  // `!draft` so pressing either the bar's `+ Card` or the create entrance below
  // shows the new card rather than the empty state it just stopped being true
  // for: `handleAddCard` sets the draft, this guard falls through, and the feed
  // below renders it with the caret already in Term. That fall-through is what
  // makes the empty state and the populated screen one flow rather than two.
  if (cards.length === 0 && !draft) {
    return (
      <div>
        <div style={{ textAlign: 'center', padding: '52px 16px', color: 'var(--text-secondary)' }}>
          <div style={{ width: 72, height: 72, margin: '0 auto 18px', background: 'var(--accent-soft)', borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)' }}>
            <Layers size={34} strokeWidth={1.8} />
          </div>
          <p style={{ margin: '0 0 6px', fontFamily: 'var(--font-content)', fontWeight: 600, fontSize: 20, color: 'var(--text-primary)' }}>
            {dd.noCardsTitle}
          </p>
          <p style={{ margin: '0 0 22px', fontSize: 13.5, lineHeight: 1.55, maxWidth: 280, marginLeft: 'auto', marginRight: 'auto' }}>
            {dd.noCardsDesc}
          </p>
          {isOwner && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', maxWidth: 280, margin: '0 auto' }}>
              <button onClick={() => handleAddCard()} style={emptyStateBtnStyle}>
                <Plus size={16} color="var(--accent)" />
                {dd.emptyCreateFirstCard}
              </button>
              <button onClick={onPasteNote} style={emptyStateBtnStyle}>
                <ClipboardPaste size={16} color="var(--accent)" />
                {dd.emptyImportText}
              </button>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div ref={feedRootRef}>
      {/* Takes over the top strip's own fixed slot rather than floating below
          it — the strip itself is unmounted by the caller (DeckDetailPage)
          for as long as selectMode is true, so this is a replacement, not an
          addition stacked on top of it (that used to overlap card content). */}
      {selectMode && (
        <div style={selectHeaderSlotStyle}>
          <SelectModeHeader
            selectedCount={selectedCount}
            totalSelectable={totalSelectable}
            onExit={exitSelectMode}
            onSelectAll={selectAllInDeck}
            selectAllDisabledReason={searching ? dd.selectAllUnavailableSearch : null}
          />
        </div>
      )}
      {/* The chip filter's own affordance (§16) — unchanged sticky-in-feed
          positioning, independent of search. */}
      {!selectMode && filterOpen && (
        <div style={stickyChromeStyle}>
          <DeckFilterChipsBar
            chip={chip}
            onChipChange={setChip}
            counts={counts}
            onClose={onCloseFilter}
          />
        </div>
      )}
      {/* Search's own panel (§15, 2026-08-04 rework) — `position: fixed`
          under the top strip, not sticky-in-flow (see DeckSearchPanel's own
          header for why sticky was the "opens partly off-screen" defect).
          Carries no chips; `chipDroppedBySearch` is only the announcement
          that search silently borrowed and will return one. */}
      {!selectMode && searchOpen && (
        <DeckSearchPanel
          ref={searchPanelRef}
          query={query}
          onQueryChange={setQuery}
          resultTotal={searchTotal ?? 0}
          status={searchStatus}
          onRetry={() => search.refetch()}
          onSearchEverywhere={handleSearchEverywhere}
          // The second exit from an empty result: the word goes into THIS deck,
          // already typed into the new card's Term.
          onAddToDeck={() => {
            const word = trimmedQuery
            onCloseSearch()
            setQuery('')
            handleAddCard(word)
          }}
          chipDroppedBySearch={searching && chipBeforeSearch !== null}
          onUndoChipDrop={() => { setQuery('') }}
          // No chip reset here any more: the chip is now an independent,
          // persistent filter (§16), and the drop/restore effect above already
          // returns it once `searching` goes false — closing the panel must
          // not additionally force it to `'all'`, or a filter the reader set
          // through the separate chip bar would be silently cleared by
          // dismissing an unrelated panel.
          onClose={() => { onCloseSearch(); setQuery('') }}
        />
      )}
      {/* The panel's own reserve. `position: fixed` takes it out of flow, so
          without this everything below — the anchor notice, a Fill-all
          progress bar, and the first result — would render starting at the
          very top of the scroller and sit behind the panel until (or unless)
          a scroll happened to clear it. This is layout, not a scroll write:
          a plain height that grows and shrinks with `searchPanelHeight`
          (empty query vs. populated, the count line, the dropped-chip
          notice), pushing this content down by exactly the panel's own
          current height rather than by a value assumed once. The flush-motion
          effect above still runs on top of this — the reserve gets the feed
          close on its own, the motion is what makes "close" exact and what
          walks a deep scroll position back up to it. */}
      {!selectMode && searchOpen && (
        <div aria-hidden style={{ height: searchPanelHeight, flexShrink: 0 }} />
      )}

      {/* The feed no longer starts at the top of the deck — a jump or a deep
          link re-anchored it. Hidden under this surface's own two modes; the
          sentence itself, and why it is owed, live in the component (which the
          desktop list now mounts too). */}
      {!selectMode && !searching && (
        <DeckAnchorNotice
          anchorPage={anchorPage}
          pageSize={pageSize}
          onBackToStart={() => onAnchorChange(1)}
        />
      )}
      {!selectMode && fillAll.batchStatus && (
        <FillAllProgressBar status={fillAll.batchStatus} stopping={fillAll.stopping} onStop={fillAll.stop} />
      )}

      <div>
        {/* No `maxHeight` / `overflowY` here: the feed is part of the page's
            flow and the page is what scrolls (ADR-067 Decision 3). The capped
            ~524px window this used to open is exactly the nested scrollport
            the ADR prohibits. */}
        <div
          style={{
            display: 'flex', flexDirection: 'column', gap: 10,
            padding: '2px 2px 4px', marginBottom: 4,
            // The pinned elements — viewing bar, edit bar, select bar — are
            // `position: fixed`, out of this flow entirely. Reserve their
            // live-measured heights here so the last card scrolls clear
            // instead of resting behind them.
            // Sum rather than re-deriving which element is showing: each
            // height is 0 unless that element is actually mounted, so this
            // reserves exactly what is pinned and nothing when none is —
            // and it cannot drift out of step with the render conditions.
            // Adding a further pinned element is one more term here, not a
            // rewrite; the multiselect replacement lands the same way.
            // `keyboardSlack` is not chrome to clear — it is scroll room the
            // §7 positioning rule needs, present only while a field is focused
            // and only as large as that field's own deficit, so it composes
            // with the measured heights above rather than standing apart from
            // them (ADR-067 Decision 4 as amended).
            // The chooser/Undo strip's own 10px gap above the edit bar (see
            // `fieldChooser` / `transientStrip` below) is only real space when
            // one of them is actually mounted — `floatingStripHeight` is 0
            // otherwise, so the gap must be conditioned on it too rather than
            // added unconditionally.
            paddingBottom: 4 + viewBarHeight + selectBarHeight + editBarHeight + keyboardSlack
              + (floatingStripHeight > 0 ? floatingStripHeight + 10 : 0),
          }}
        >
          {/* While searching, the empty/error/loading states belong to the
              search bar — it is where §15's two exits live, and printing a
              second "nothing here" under them would say it twice and offer
              nothing either time. */}
          {feedCards.length === 0 && !draft ? (
            searching ? null : (
              <div style={{ padding: '40px 20px', textAlign: 'center', fontFamily: 'var(--font-ui)', fontSize: 13.5, color: 'var(--text-secondary)' }}>
                {tr.deckManagement.noCardsMatchFilter}
              </div>
            )
          ) : (
            feedCards.map((card, index) => {
              const isActive = card.id === activeCardId
              // Neighbours recede but keep EVERY PIXEL of their height (§5.3):
              // dimming and desaturation only, never a collapse — a card above
              // the active one is cut off by the edge of the screen, never
              // squeezed, so nothing moves under the finger. `saturate(1)`
              // rather than `none` on the way back so the transition animates
              // in both directions.
              const dimmed = activeCardId !== null && !isActive
              return (
                <div
                  key={card.id}
                  // The row element, published to the registry the deep link's
                  // arrival moves to. One write per row from the render that
                  // makes it, so the arrival never has to ask the document for
                  // a node (ADR-067 Decision 6).
                  ref={(el) => {
                    if (el) rowElsRef.current.set(card.id, el)
                    else rowElsRef.current.delete(card.id)
                  }}
                  // Only editing mode can dim, so only editing mode pays for a
                  // filter: `saturate(1)` on every card of a 1000-card deck
                  // would be a compositing layer per row for nothing.
                  style={editingMode ? {
                    opacity: dimmed ? 0.55 : 1,
                    filter: dimmed ? 'saturate(0.2)' : 'saturate(1)',
                    transition: 'opacity 180ms ease-out, filter 180ms ease-out',
                  } : undefined}
                >
                  {isActive ? (
                    <ActiveVocabCard
                      ref={activeCardHandleRef}
                      card={card}
                      deckId={deckId}
                      deckLanguage={deckLanguage}
                      cardNumber={deckNumberOf(index)}
                      initialFieldKey={activation?.fieldKey ?? null}
                      initialCaret={activation?.caret ?? null}
                      onToggleFieldChooser={handleToggleFieldChooser}
                      fieldChooserOpen={fieldChooserOpen}
                      onFieldFocus={handleFieldFocus}
                      onFieldBlur={handleFieldBlur}
                      onFocusedFieldType={handleFocusedFieldType}
                      missingFieldType={missingFieldDescriptor}
                      onFieldDeleted={showFieldUndo}
                      onFieldOrderChanged={handleFieldOrderChanged}
                      onSaveError={showActiveSaveError}
                    />
                  ) : (
                    <VocabCard
                      card={card}
                      deckLanguage={deckLanguage}
                      isOwner={isOwner}
                      // The deep link's mark. Styling only, and deliberately
                      // not tied to ownership or to editing mode: arriving
                      // from a lookup has to work on a deck the reader can
                      // only read.
                      highlighted={card.id === highlightedCardId}
                      onLongPress={isOwner && !selectMode ? () => selectCard(card.id) : undefined}
                      selectMode={isOwner && selectMode}
                      selected={isCardSelected(selection, card.id)}
                      onToggleSelect={() => toggleSelect(card.id)}
                      editingMode={isOwner && editingMode && !selectMode}
                      onActivateField={(fieldKey, caret) => setActivation({ cardId: card.id, fieldKey, caret })}
                    />
                  )}
                </div>
              )
            })
          )}

          {/* The unsaved new card, always last — `+ Card` appends (§10). It is
              rendered outside the list rather than spliced into it so nothing
              here can renumber or reorder a stored card. */}
          {draft && draftCard && (
            <ActiveVocabCard
              key={draft.key}
              ref={activeCardHandleRef}
              card={draftCard}
              deckId={deckId}
              onCreated={(created) => setDraft((prev) => (prev ? { ...prev, savedId: created.id } : prev))}
              deckLanguage={deckLanguage}
              // Counted off the DECK, not the rendered feed: `+ Card` appends
              // at max(position)+1 over the whole deck, so on an anchored
              // window (or any partially loaded feed) the feed's own length
              // would name a position the card will not take. `deckCardCount`
              // comes from the deck record and is right before anything has
              // been scrolled; the saved copy is filtered out of the feed, so
              // there is no flicker while the refetch is in flight either.
              cardNumber={deckCardCount + 1}
              initialFieldKey={TERM_FIELD_KEY}
              initialCaret={null}
              onToggleFieldChooser={handleToggleFieldChooser}
              fieldChooserOpen={fieldChooserOpen}
              onFieldFocus={handleFieldFocus}
              onFieldBlur={handleFieldBlur}
              onFieldDeleted={showFieldUndo}
              onFieldOrderChanged={handleFieldOrderChanged}
              onSaveError={showActiveSaveError}
            />
          )}
          <div ref={sentinelRef} aria-hidden="true" style={{ height: 1 }} />
        </div>

        {showJumpToLatest && !selectMode && !editingMode && (
          <button
            onClick={() => scrollToBottom('smooth')}
            aria-label={dd.jumpToLatestBtn}
            style={{
              // Viewport-relative, like the bar it sits above: the feed is no
              // longer a box with a bottom edge of its own to anchor to.
              position: 'fixed', zIndex: 21,
              // Rides above the viewing bar — the only pinned element that can
              // be mounted at the same time as this affordance.
              bottom: `calc(${pinnedBottom} + ${viewBarHeight + 12}px)`,
              left: '50%', transform: 'translateX(-50%)',
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none',
              borderRadius: 'var(--radius-pill)', padding: '8px 16px',
              fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600,
              cursor: 'pointer', boxShadow: 'var(--shadow-float)',
            }}
          >
            <ArrowDown size={14} />
            {dd.jumpToLatestBtn}
          </button>
        )}

        {fillAllOpen && (
          <FillAllSheet
            preflight={fillAll.preflight}
            loading={fillAll.loading}
            error={fillAll.preflightError}
            starting={fillAll.starting}
            onConfirm={fillAll.confirm}
            onCancel={onCloseFillAll}
          />
        )}
        {fillSelection.sheetOpen && (
          <FillAllSheet
            preflight={fillSelection.preflight}
            loading={fillSelection.loading}
            error={fillSelection.preflightError}
            starting={false}
            onConfirm={fillSelection.confirm}
            onCancel={fillSelection.cancel}
            nothingToFillMessage={tr.deckManagement.fillSelectionNothingToFill}
          />
        )}
      </div>

      {arrivalStrip}
      {viewBar}
      {editBar}
      {fieldChooser}

      {jumpStrip}
      {selectBar}
      {transientStrip}
      {/* A keyboard/viewport diagnostic probe (`components/dev/KeyboardProbe.tsx`)
          used to mount here, gated on `?kbdebug=1` rather than `import.meta.env.DEV`
          because the numbers that matter come from the installed TWA — a production
          build, where DEV is false. That argument still holds and is still unserved;
          removed 2026-08-07 (docs/diagnostics/item-9-dead-code-inventory-2026-08-07.md
          §2) because its `data-kb-probe="composer"` query target went out with the
          composer (ADR-067 Decision 5), so every number it read had resolved to `—`
          since. Recoverable from git. If rebuilt, the marker attribute belongs on
          this bar (editBar/viewBar), not on a composer — there is no composer any
          more. */}
    </div>
  )
}

/**
 * The band of the layout viewport left free for a focused field: below the top
 * strip (and below whatever the visual viewport has been panned to), above the
 * keyboard and above every pinned element currently in the bottom slot.
 *
 * Entirely measured at runtime — the strip's rendered bottom, the visual
 * viewport's own edges, and the live rects of the pinned elements. ADR-067
 * forbids a hand-tuned constant standing in for rendered chrome, and the
 * amendment records that the absolute size of this band is no longer
 * load-bearing for positioning at all: the rule reads it at focus time and
 * moves by the deficit.
 *
 * Coordinates are layout-viewport relative throughout — the same space
 * `getBoundingClientRect()` reports for the focused row, and the space the
 * pinned elements are positioned in (they are `fixed`, already offset by the
 * keyboard band via `useViewportBottomGap`), so nothing is double-counted.
 *
 * Returns null where nothing can be measured (jsdom, SSR, or a degenerate band),
 * which the callers read as "do not move the page".
 */
function measureFreeSpace(): { top: number; bottom: number } | null {
  if (typeof document === 'undefined') return null
  const vv = window.visualViewport
  const viewportTop = vv ? vv.offsetTop : 0
  const viewportBottom = vv ? vv.offsetTop + vv.height : window.innerHeight

  const strip = document.querySelector(`[${DECK_TOP_STRIP_PROBE_ATTR}]`)
  const stripBottom = strip ? strip.getBoundingClientRect().bottom : DECK_TOP_STRIP_HEIGHT
  const top = Math.max(viewportTop, stripBottom) + FOCUS_TOP_GAP

  let bottom = viewportBottom
  document.querySelectorAll(`[${PINNED_PROBE_ATTR}]`).forEach((el) => {
    const rect = el.getBoundingClientRect()
    // A zero-height node is an element that has not been laid out yet, not one
    // sitting on the bottom edge — reading its top as 0 would collapse the band.
    if (rect.height > 0) bottom = Math.min(bottom, rect.top)
  })

  return bottom - top > 0 ? { top, bottom } : null
}

const stickyChromeStyle: CSSProperties = {
  position: 'sticky', top: DECK_TOP_STRIP_CLEARANCE, zIndex: 6,
  background: 'var(--bg-page)',
}

// Same fixed slot as DeckTopStrip itself (position/zIndex/top match exactly)
// — the selection header occupies the strip's place, it doesn't sit near it.
const selectHeaderSlotStyle: CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, zIndex: 25,
}

/** The empty deck's two entrances share one style because the brief makes them
 * equal ("два равноправных входа"). There is no primary variant any more: a
 * filled capsule on one of them would rank a choice the brief deliberately
 * leaves to the author. */
const emptyStateBtnStyle: CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  background: 'var(--bg-surface)',
  color: 'var(--text-primary)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-pill)', padding: '12px 18px',
  fontFamily: 'var(--font-ui)', fontWeight: 600, fontSize: 14,
  cursor: 'pointer',
}

// Quick jump's confirm (§13). Solid, like every other terminal action in this
// surface's chrome — it moves the reader somewhere, which is the strongest
// thing a control in this slot does.
const jumpGoStyle: CSSProperties = {
  flexShrink: 0,
  height: 36, padding: '0 14px',
  background: 'var(--control-solid)', color: 'var(--control-solid-text)',
  border: 'none', borderRadius: 'var(--radius-pill)', cursor: 'pointer',
  fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600,
}
